import React from 'react';
import { Button } from 'react-covideo-common';

import styled, { useTheme } from 'styled-components/macro';
import { VIDEO_QUALITY } from '../../context/record/types';
import { useRecordSettings } from '../../context/record/provider';
import { ParagraphNormal, ParagraphSmall } from '../../components/typography';
import { Gap, Stack } from '../../components/layout';
import { EnhancedDropdown } from '../../components/EnhancedDropdown';
import { RadioInput } from '../../components/RadioInput';
import { NewModal } from '../../components/NewModal';
import { useTranslation } from 'react-i18next';

const RadioLabel = styled.label`
  display: flex;
  gap: 4px;
  color: ${props => props.theme.colors.black[100]};
  cursor: pointer;
`;

interface IProps {
  handleModalClose: () => void;
}

type Option = {
  label: string;
  value: string;
};

export const ModalRecordingVideosSettings = ({ handleModalClose }: IProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const {
    recorderSettings,
    setUserSettings,
    userSettings,
    onSubmitModalHandler,
    initialUserSettings,
  } = useRecordSettings();

  const onSubmit = () => {
    onSubmitModalHandler();
    handleModalClose();
  };

  const onCancelOrCloseModalSettings = () => {
    setUserSettings(initialUserSettings);
    handleModalClose();
  };

  const onChangeVideoSettingsHandler = (value: Option) =>
    setUserSettings({
      ...userSettings,
      videoSource: value,
    });

  const onChangeAudioSettingsHandler = (value: Option) =>
    setUserSettings({
      ...userSettings,
      audioSource: value,
    });

  const onVideoQualityHandler = (videoQuality: VIDEO_QUALITY) =>
    setUserSettings({
      ...userSettings,
      videoQuality,
    });

  return (
    <NewModal
      headerText={t('Recording settings')}
      closeModal={onCancelOrCloseModalSettings}
      style={{
        content: {
          maxWidth: '592px',
        },
      }}
    >
      <Gap>
        {!!recorderSettings.videoSource.length && (
          <Stack width='100%' alignItems='start' gap='4px'>
            <ParagraphSmall color={colors.neutral[60]} as='p'>
              {t('Video source')}
            </ParagraphSmall>
            <EnhancedDropdown
              value={userSettings.videoSource}
              options={recorderSettings.videoSource}
              onSelectOption={onChangeVideoSettingsHandler}
            />
          </Stack>
        )}
        {!!recorderSettings.audioSource.length && (
          <Stack width='100%' alignItems='start' gap='4px'>
            <ParagraphSmall color={colors.neutral[60]} as='p'>
              {t('Audio source')}
            </ParagraphSmall>
            <EnhancedDropdown
              value={userSettings.audioSource}
              options={recorderSettings.audioSource}
              onSelectOption={onChangeAudioSettingsHandler}
            />
          </Stack>
        )}
        <>
          <ParagraphSmall color={colors.neutral[60]} as='p'>
            {t('Video Quality')}
          </ParagraphSmall>
          <Stack alignItems='flex-start' width='100%' gap='8px' m='0 0 16px 0'>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.LOW}
                  name='quality'
                  value={VIDEO_QUALITY.LOW}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.LOW)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.LOW)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('Low')}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall as='p'>{t('Faster Upload')}</ParagraphSmall>
            </Gap>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.STANDARD}
                  name='quality'
                  value={VIDEO_QUALITY.STANDARD}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.STANDARD)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.STANDARD)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('Standard')}{' '}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall as='p'>{t('Fast Upload')}</ParagraphSmall>
            </Gap>
            <Gap width='100%' justifyContent='space-between'>
              <RadioLabel>
                <RadioInput
                  checked={userSettings.videoQuality === VIDEO_QUALITY.HIGH}
                  name='quality'
                  value={VIDEO_QUALITY.HIGH}
                  onClick={() => onVideoQualityHandler(VIDEO_QUALITY.HIGH)}
                  onChange={() => onVideoQualityHandler(VIDEO_QUALITY.HIGH)}
                />
                <ParagraphNormal color={colors.neutral[100]}>
                  {t('High')}
                </ParagraphNormal>
              </RadioLabel>
              <ParagraphSmall>{t('Slow Upload')}</ParagraphSmall>
            </Gap>
          </Stack>
        </>
      </Gap>
      <Gap width='100%' justifyContent='flex-end'>
        <Button
          variant='secondary'
          text={t('Cancel')}
          onClick={onCancelOrCloseModalSettings}
        />
        <Button text={t('Save')} onClick={onSubmit} />
      </Gap>
    </NewModal>
  );
};
